import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Empty, Form, InputNumber, Pagination, Row, Space, Spin, Tag, Typography, message } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useDrag, useDrop } from "react-dnd";
import { SaveButton, InputTextArea, InputCheck, CancelButton, ModalComponent, SelectWithSearch, SelectResourceList, trans, InputDate, InputText } from "@comps/components";
import { makeRequestStateless } from "@utils/helpers";
import { createSelectList } from "@comps/commonFunctions";
import { createDataReq, getEmployeeFilterReq, getEmployeeListReq } from "../requests";
import { Commons, Const } from "../../../constants";

const langKey = Const["payitem"].lngKey;
const formName = "createPayitem";
const EMPLOYEE_CARD_TYPE = "custom-payitem-employee-card";

const EmployeeCard = ({ employee, panel, onMove, onAmountChange, showAmountError }) => {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: EMPLOYEE_CARD_TYPE,
    item: { employee, fromPanel: panel },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [employee, panel]);

  drag(ref);

  return (
    <div
      ref={ ref }
      onClick={ () => onMove(employee, panel === "selected" ? "unselected" : "selected") }
      style={ {
        border: "1px solid #D8E3F0",
        borderRadius: 10,
        background: "#FFFFFF",
        padding: 14,
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        cursor: "pointer",
        opacity: isDragging ? 0.45 : 1,
        minHeight: 84,
      } }
    >
      <div
        style={ {
          width: 40,
          height: 40,
          minWidth: 40,
          borderRadius: "50%",
          background: "#E7F0FF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        } }
      >
        <img
          src={ employee.profile_pic_thumb_url && employee.profile_pic_thumb_url !== "images/arnold-avatar.jpg" ? employee.profile_pic_thumb_url : "/images/arnold-avatar.jpg" }
          alt={ employee.full_name_code || employee.full_name || "Employee" }
          style={ { width: "100%", height: "100%", objectFit: "cover" } }
        />
      </div>

      <div style={ { flex: 1, minWidth: 0 } }>
        <div style={ { fontWeight: 600, color: "#223052", marginBottom: 4, wordBreak: "break-word" } }>
          { employee.full_name_code || employee.full_name || employee.employee_name }
        </div>
        <div style={ { color: "#6B7280", fontSize: 12, marginBottom: panel === "selected" ? 10 : 0 } }>
          { employee.designation_name || employee.designation?.name || employee.department_name || "Employee" }
        </div>

        { panel === "selected" && (
          <div onClick={ (event) => event.stopPropagation() }>
            <InputNumber
              style={ { width: "100%" } }
              min={ 0 }
              value={ employee.amount === "" ? null : Number(employee.amount) }
              placeholder={ "Amount" }
              status={ showAmountError ? "error" : "" }
              onChange={ (value) => onAmountChange(employee.id, value) }
            />
            { showAmountError && (
              <div style={ { color: "#ff4d4f", fontSize: 12, marginTop: 4 } }>
                Amount is required
              </div>
            ) }
          </div>
        ) }
      </div>
    </div>
  );
};

const EmployeePanel = ({ title, count, panel, employees, onMove, onMoveAll, onAmountChange, amountErrors, pagination, onPageChange }) => {
  const ref = useRef(null);

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: EMPLOYEE_CARD_TYPE,
    canDrop: (item) => item.fromPanel !== panel,
    drop: (item) => {
      if (item.fromPanel !== panel) {
        onMove(item.employee, panel);
      }
      return { moved: true };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [panel, onMove]);

  drop(ref);

  return (
    <div
      ref={ ref }
      style={ {
        border: "1px solid #CFE0F3",
        borderRadius: 14,
        minHeight: 360,
        background: isOver && canDrop ? "#F2F8FF" : "#FFFFFF",
        transition: "background-color 0.18s ease, border-color 0.18s ease",
      } }
    >
      <div
        style={ {
          padding: "18px 20px",
          borderBottom: "1px solid #DCE7F3",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        } }
      >
        <Typography.Title level={ 4 } style={ { margin: 0, fontSize: 18, color: "#2A2F3A" } }>
          { title } ({ count })
        </Typography.Title>

        <Button
          //shape="round"
          onClick={ onMoveAll }
          disabled={ employees.length === 0 }
        //style={ { width: 36, height: 46, borderColor: "#C9D6DF", color: "#7A8798" } }
        >
          { panel === "selected" ? <ArrowLeftOutlined /> : <ArrowRightOutlined /> }
        </Button>
      </div>

      <div
        style={ {
          padding: 16,
          minHeight: 286,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 16,
          alignContent: "start",
        } }
      >
        { employees.length === 0 ? (
          <div style={ { gridColumn: "1 / -1", paddingTop: 24 } }>
            <Empty description={ panel === "selected" ? "No selected employees" : "No unselected employees" } />
          </div>
        ) : employees.map((employee) => (
          <EmployeeCard
            key={ employee.id }
            employee={ employee }
            panel={ panel }
            onMove={ onMove }
            onAmountChange={ onAmountChange }
            showAmountError={ panel === "selected" && amountErrors.has(employee.id) }
          />
        )) }
      </div>

      { panel === "unselected" && pagination && pagination.total > pagination.pageSize && (
        <div style={ { padding: "0 16px 16px", display: "flex", justifyContent: "flex-end" } }>
          <Pagination
            size="small"
            current={ pagination.current }
            pageSize={ pagination.pageSize }
            total={ pagination.total }
            onChange={ onPageChange }
            showSizeChanger={ false }
          />
        </div>
      ) }
    </div>
  );
};

const CreateCustomPayitem = (props) => {
  const [saving, setSaving] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    branches: [],
    departments: [],
    grades: [],
    designations: [],
  });
  const [processed, setProcessed] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [amountErrors, setAmountErrors] = useState(new Set());
  const [employeePagination, setEmployeePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const selectedLocationId = Form.useWatch("location_id", filterForm);

  const companyId = localStorage.getItem("company_id") ? localStorage.getItem("company_id") : 1;

  useEffect(() => {
    loadFilterOptions();
    loadEmployees({});
    // eslint-disable-next-line
  }, []);

  const branchOptions = useMemo(() => {
    if (!selectedLocationId) {
      return createSelectList(filterOptions.branches);
    }

    return createSelectList(
      (filterOptions.branches || []).filter((branch) => !branch.location_id || branch.location_id === selectedLocationId)
    );
  }, [filterOptions.branches, selectedLocationId]);

  const mapEmployee = (employee) => ({
    ...employee,
    amount: employee.amount ?? form.getFieldValue("default_amount") ?? "",
  });

  const syncDefaultAmountField = (employees) => {
    if (!employees || employees.length === 0) {
      form.setFieldValue("default_amount", undefined);
      return;
    }

    const normalizedAmounts = employees.map((employee) => (
      employee.amount === "" || employee.amount === null || employee.amount === undefined
        ? ""
        : String(employee.amount)
    ));

    const firstAmount = normalizedAmounts[0];
    const allSame = normalizedAmounts.every((amount) => amount === firstAmount && amount !== "");

    form.setFieldValue("default_amount", allSame ? Number(firstAmount) : undefined);
  };

  const selectedEmployeeIds = useMemo(
    () => new Set(selectedEmployees.map((employee) => employee.id)),
    [selectedEmployees]
  );

  const unselectedEmployees = useMemo(
    () => availableEmployees.filter((employee) => !selectedEmployeeIds.has(employee.id)),
    [availableEmployees, selectedEmployeeIds]
  );

  const buildFilterPayload = (values = {}, page = 1) => {
    const filters = ["employee_status_|active"];

    if (props.employment_id) {
      filters.push(`ids_|${props.employment_id}`);
    }

    if (values.employment_ids && values.employment_ids.length) {
      filters.push(`ids_|${values.employment_ids.join(",")}`);
    }
    if (values.location_id) {
      filters.push(`location_|${values.location_id}`);
    }
    if (values.designation_id) {
      filters.push(`designation_|${values.designation_id}`);
    }
    if (values.grade_id) {
      filters.push(`grade_|${values.grade_id}`);
    }
    if (values.department_id) {
      filters.push(`department_|${values.department_id}`);
    }
    if (values.branch_id) {
      filters.push(`branch_|${values.branch_id}`);
    }

    return {
      page,
      filters: filters.join(";"),
    };
  };

  const loadFilterOptions = () => {
    setFilterLoading(true);
    makeRequestStateless(getEmployeeFilterReq, null, (response) => {
      setFilterOptions({
        locations: response?.organization_structure_data?.location || [],
        branches: response?.organization_structure_data?.branch || [],
        departments: response?.organization_structure_data?.department || [],
        grades: response?.supporting_data?.grade || [],
        designations: response?.supporting_data?.designation || [],
      });
      setFilterLoading(false);
    }, () => {
      setFilterLoading(false);
    });
  };

  const loadEmployees = (values = {}, page = 1) => {
    setEmployeesLoading(true);
    makeRequestStateless(getEmployeeListReq, buildFilterPayload(values, page), (response) => {
      const list = (response?.employee_employments?.data || []).map(mapEmployee);
      const meta = response?.employee_employments?.meta?.pagination || {};
      setAvailableEmployees(list);
      setEmployeePagination({
        current: meta.current_page || page,
        pageSize: meta.per_page || 10,
        total: meta.total || list.length,
      });

      if (props.employment_id) {
        const match = list.find((employee) => employee.id === props.employment_id);
        if (match) {
          setSelectedEmployees([match]);
        }
      }

      setEmployeesLoading(false);
    }, (err, res) => {
      setEmployeesLoading(false);
      message.error(res?.response?.data?.message || "Unable to load employees");
    });
  };

  const moveEmployee = (employee, targetPanel) => {
    if (targetPanel === "selected") {
      setSelectedEmployees((current) => (
        current.some((item) => item.id === employee.id)
          ? current
          : [...current, mapEmployee(employee)]
      ));
      return;
    }

    if (props.employment_id) {
      return;
    }

    setSelectedEmployees((current) => current.filter((item) => item.id !== employee.id));
    setAmountErrors((current) => {
      const nextErrors = new Set(current);
      nextErrors.delete(employee.id);
      return nextErrors;
    });
  };

  const moveAllToSelected = () => {
    setSelectedEmployees((current) => {
      const nextMap = new Map(current.map((employee) => [employee.id, employee]));
      unselectedEmployees.forEach((employee) => {
        nextMap.set(employee.id, nextMap.get(employee.id) || mapEmployee(employee));
      });
      return Array.from(nextMap.values());
    });
  };

  const moveAllToUnselected = () => {
    if (props.employment_id) {
      return;
    }
    setAmountErrors(new Set());
    setSelectedEmployees([]);
  };

  const updateSelectedAmount = (employeeId, value) => {
    setSelectedEmployees((current) => {
      const nextEmployees = current.map((employee) => (
        employee.id === employeeId ? { ...employee, amount: value ?? "" } : employee
      ));
      syncDefaultAmountField(nextEmployees);
      return nextEmployees;
    });
    setAmountErrors((current) => {
      const nextErrors = new Set(current);
      if (value === "" || value === null || value === undefined) {
        nextErrors.add(employeeId);
      } else {
        nextErrors.delete(employeeId);
      }
      return nextErrors;
    });
  };

  const applyDefaultAmount = () => {
    const defaultAmount = form.getFieldValue("default_amount");
    if (defaultAmount === undefined || defaultAmount === null || defaultAmount === "") {
      message.warning("Enter a default amount first");
      return;
    }

    setSelectedEmployees((current) => current.map((employee) => ({
      ...employee,
      amount: defaultAmount,
    })));
    setAmountErrors(new Set());
  };

  const onApplyFilter = (values) => {
    setAppliedFilters(values);
    loadEmployees(values, 1);
  };

  const onResetFilter = () => {
    const nextValues = props.employment_id ? { employment_ids: [props.employment_id] } : {};
    filterForm.resetFields();
    filterForm.setFieldsValue(nextValues);
    setAppliedFilters(nextValues);
    loadEmployees(nextValues, 1);
  };

  const onChangeUnselectedPage = (page) => {
    loadEmployees(appliedFilters, page);
  };

  const validateSelectedEmployees = () => {
    if (selectedEmployees.length === 0) {
      message.error("Select at least one employee");
      return false;
    }

    const invalidEmployees = selectedEmployees.filter((employee) => employee.amount === "" || employee.amount === null || employee.amount === undefined);
    if (invalidEmployees.length > 0) {
      setAmountErrors(new Set(invalidEmployees.map((employee) => employee.id)));
    } else {
      setAmountErrors(new Set());
    }

    const invalidEmployee = invalidEmployees[0];
    if (invalidEmployee) {
      message.error(`Enter amount for ${invalidEmployee.full_name_code || invalidEmployee.full_name || invalidEmployee.employee_name}`);
      return false;
    }

    return true;
  };

  const onSubmit = async (data) => {
    if (!validateSelectedEmployees()) {
      return;
    }

    setSaving(true);
    const successes = [];
    const failures = [];

    for (const employee of selectedEmployees) {
      const payload = {
        custom_payitem: {
          employment_id: employee.id,
          amount: Number(employee.amount),
          processed,
          date: data.date.format("YYYY-MM-DD"),
          description: data.description,
          company_id: companyId,
        },
        payitemId: props.payitemId,
      };

      try {
        const response = await createDataReq(payload);
        successes.push(response?.data);
      } catch (error) {
        failures.push({
          employee_name: employee.full_name_code || employee.full_name || employee.employee_name,
          message: error?.response?.data?.message || "Unable to save record",
        });
      }
    }

    setSaving(false);

    if (successes.length > 0) {
      message.success(`Created ${successes.length} custom payitem${successes.length > 1 ? "s" : ""}`);
      props.onCompleted?.(true);
    }

    if (failures.length > 0) {
      message.warning(`${failures.length} employee record${failures.length > 1 ? "s" : ""} could not be created`);
      if (successes.length === 0) {
        const failureList = failures.slice(0, 3).map((failure) => `${failure.employee_name}: ${failure.message}`).join(" | ");
        message.error(failureList);
      }
    }
  };

  const footer = [
    <SaveButton loading={ saving } form={ formName } key="create_button" htmlType="submit" />,
    <CancelButton key="close_button" onClick={ () => props.onCompleted(false) } />,
  ];

  return (
    <ModalComponent
      top={ 20 }
      width={ 1300 }
      maskClosable={ false }
      mainTitle={ trans(Commons.create) }
      subTitle={ trans(`${langKey}.moduleName`) }
      visible={ true }
      footer={ footer }
      onCancel={ () => props.onCompleted(false) }
    >
      <Form { ...formLayout } name={ formName } onFinish={ onSubmit } form={ form } initialValues={ { processed: false } }>
        <Row gutter={ 20 }>
          <Col lg={ 8 } md={ 12 } xs={ 24 }>
            <Form.Item name="date" rules={ rules.date } label={ trans(`${langKey}.date`) } className="da-mb-16">
              <InputDate />
            </Form.Item>
          </Col>

          <Col lg={ 8 } md={ 12 } xs={ 24 }>
            <Form.Item name="default_amount" label="Default Amount" className="da-mb-16">
              <InputText type="number" onWheel={ (event) => event.target.blur() } placeholder="Default amount for selected employees" />
            </Form.Item>
          </Col>

          <Col lg={ 8 } md={ 12 } xs={ 24 }>
            <Form.Item
              name="processed"
              label={ trans(`${langKey}.processed`) }
              className="da-mb-16"
              valuePropName="checked"
            >
              <InputCheck onChange={ (event) => setProcessed(event.target.checked) } disabled />
            </Form.Item>
          </Col>

          <Col span={ 24 }>
            <Form.Item name="description" label={ trans(`${langKey}.description`) } className="da-mb-20">
              <InputTextArea />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <div style={ { padding: 20, border: "1px solid #C9D6DF", borderRadius: 12, background: "rgba(201, 214, 223, 0.05)", marginBottom: 24 } }>
        <Form
          { ...formLayout }
          name="customPayitemFilterForm"
          form={ filterForm }
          onFinish={ onApplyFilter }
          initialValues={ props.employment_id ? { employment_ids: [props.employment_id] } : {} }
        >
          <Row gutter={ 20 }>
            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="employment_ids" label="Employment" className="da-mb-16">
                <SelectResourceList type="employment" mode="multiple" disabled={ !!props.employment_id } />
              </Form.Item>
            </Col>

            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="location_id" label="Location" className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select Location"
                  options={ createSelectList(filterOptions.locations) }
                  loading={ filterLoading }
                  onChange={ () => filterForm.setFieldValue("branch_id", undefined) }
                  disabled={ !!props.employment_id }
                />
              </Form.Item>
            </Col>

            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="designation_id" label="Designation" className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select Designation"
                  options={ createSelectList(filterOptions.designations) }
                  loading={ filterLoading }
                  disabled={ !!props.employment_id }
                />
              </Form.Item>
            </Col>

            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="grade_id" label="Grade" className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select Grade"
                  options={ createSelectList(filterOptions.grades) }
                  loading={ filterLoading }
                  disabled={ !!props.employment_id }
                />
              </Form.Item>
            </Col>

            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="department_id" label="Department" className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select Department"
                  options={ createSelectList(filterOptions.departments, { key: "department", filterKey: "id" }) }
                  loading={ filterLoading }
                  disabled={ !!props.employment_id }
                />
              </Form.Item>
            </Col>

            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="branch_id" label="Branch" className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select Branch"
                  options={ branchOptions }
                  loading={ filterLoading }
                  disabled={ !!props.employment_id }
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={ { display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 16, borderTop: "1px solid #DCE7F3", flexWrap: "wrap" } }>
            <Button danger onClick={ onResetFilter } disabled={ !!props.employment_id }>Reset Filter</Button>
            <Button type="primary" htmlType="submit" loading={ employeesLoading } disabled={ !!props.employment_id }>Apply Filter</Button>
          </div>
        </Form>
      </div>

      <Spin spinning={ employeesLoading } tip="Loading...">
        <div style={ { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 12, flexWrap: "wrap" } }>
          <Space wrap>
            <Tag color="blue">{ selectedEmployees.length } selected</Tag>
            <Button onClick={ applyDefaultAmount } disabled={ selectedEmployees.length === 0 }>
              Apply Default Amount
            </Button>
          </Space>
          <Typography.Text type="secondary">
            Drag employee cards between sections or click a card to move it.
          </Typography.Text>
        </div>

        <Row gutter={ 28 }>
          <Col lg={ 12 } sm={ 24 } xs={ 24 }>
            <EmployeePanel
              title="Unselected Employees"
              count={ unselectedEmployees.length }
              panel="unselected"
              employees={ unselectedEmployees }
              onMove={ moveEmployee }
              onMoveAll={ moveAllToSelected }
              onAmountChange={ updateSelectedAmount }
              amountErrors={ amountErrors }
              pagination={ employeePagination }
              onPageChange={ onChangeUnselectedPage }
            />
          </Col>

          <Col lg={ 12 } sm={ 24 } xs={ 24 }>
            <EmployeePanel
              title="Selected Employees"
              count={ selectedEmployees.length }
              panel="selected"
              employees={ selectedEmployees }
              onMove={ moveEmployee }
              onMoveAll={ moveAllToUnselected }
              onAmountChange={ updateSelectedAmount }
              amountErrors={ amountErrors }
            />
          </Col>
        </Row>
      </Spin>
    </ModalComponent>
  );
};

export default CreateCustomPayitem;

const rules = {
  date: [
    { required: true, message: trans(`${langKey}.date`) },
  ],
};

const formLayout = {
  layout: "vertical",
  labelCol: { span: 6 },
  wrapperCol: { span: 24 },
  labelAlign: "left",
};
